import { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/system';
import { COLOR_PRIMARY_LIGHT } from '../Colors';

/**
 * CSS selector for MUI DataGrid selected rows, including hovered selected rows.
 */
const MUI_SELECTED_ROW_HIGHLIGHT_SELECTOR = '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover';

/**
 * CSS selector for MUI DataGrid pinned-right cells in selected rows, including hovered selected rows.
 * This is used to override the background color of pinned-right cells in selected rows, which can otherwise
 * appear darker/lighter than the rest of the row due to MUI's default styling or local overrides.
 */
const MUI_SELECTED_ROW_ACTIONS_CELL_SELECTOR =
    '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight';

/**
 * Opacity values used to tint selected rows for dark and light modes.
 */
export const SELECTED_ROW_HIGHLIGHT_OPACITY = {
    darkMode: 0.08,
    lightMode: 0.16
} as const;

/**
 * Returns the selected-row highlight color based on the active theme mode.
 *
 * @param isDarkMode Whether the current theme is dark mode.
 * @returns RGBA color string used for selected row highlighting.
 */
export const getSelectedRowHighlightColor = (isDarkMode: boolean): string =>
    alpha(
        COLOR_PRIMARY_LIGHT,
        isDarkMode ? SELECTED_ROW_HIGHLIGHT_OPACITY.darkMode : SELECTED_ROW_HIGHLIGHT_OPACITY.lightMode
    );

/**
 * Builds sx overrides that apply selected-row highlight styles to MUI DataGrid rows.
 *
 * @param isDarkMode Whether the current theme is dark mode.
 * @returns sx style object targeting selected and selected-hover row states.
 */
export const getSelectedRowHighlightSx = (isDarkMode: boolean): SxProps<Theme> => {
    const selectedRowColor = getSelectedRowHighlightColor(isDarkMode);

    const sx: SxProps<Theme> = {
        [MUI_SELECTED_ROW_HIGHLIGHT_SELECTOR]: {
            backgroundColor: selectedRowColor
        },
        [MUI_SELECTED_ROW_ACTIONS_CELL_SELECTOR]: {
            backgroundColor: 'transparent'
        }
    };

    return sx;
};
